import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { EventType, Router } from '@angular/router';
import { AlertService } from '@seatix/shared-util-alert';
import { injectAppVersion } from '@seatix/shared-util-app-version';
import { DeploymentRefreshDialogComponent } from './deployment-refresh-dialog/deployment-refresh-dialog.component';

/**
 * Handles automatic application refresh when chunk loading fails due to version skew.
 * Listens to Vite's preload errors and gracefully reloads the app when a new version is deployed.
 */
@Injectable({ providedIn: 'root' })
export class AppVersionRefreshHandlerService {
  readonly #document = inject(DOCUMENT);
  readonly #platform = inject(PLATFORM_ID);
  readonly #router = inject(Router);
  readonly #dialog = inject(MatDialog);
  readonly #appVersion = injectAppVersion();
  readonly #alert = inject(AlertService);

  #window?: Window;

  readonly #MAX_RETRIES = 2;
  readonly #RETRY_DELAY_MS = 5000;
  readonly #RETRY_KEY = 'chunk_error_retry';
  readonly #RETRY_TIMESTAMP_KEY = 'chunk_error_timestamp';
  readonly #VERSION_KEY = 'chunk_error_version';

  /**
   * Initializes the chunk error handler.
   * Must be called during app initialization (via APP_INITIALIZER).
   */
  initialize(): void {
    if (!isPlatformBrowser(this.#platform)) return;

    this.#window = this.#document.defaultView as Window;
    if (!this.#window) return;

    this.#clearRetryIfVersionChanged();
    this.#setupNavigationErrorHandler();
    this.#setupVitePreloadErrorHandler();
    this.#setupUnhandledRejectionHandler();
  }

  #setupNavigationErrorHandler(): void {
    this.#router.events.subscribe((event) => {
      if (
        event.type === EventType.NavigationError &&
        this.#isNavigationChunkError(event)
      ) {
        this.#handleChunkError();
      }
    });
  }

  #setupVitePreloadErrorHandler(): void {
    this.#window?.addEventListener('vite:preloadError', (event: Event) => {
      event.preventDefault();
      this.#handleChunkError();
    });
  }

  #setupUnhandledRejectionHandler(): void {
    this.#window?.addEventListener('unhandledrejection', (event) => {
      if (this.#isChunkLoadRejection(event)) {
        event.preventDefault();
        this.#handleChunkError();
      }
    });
  }

  #isNavigationChunkError(event: unknown): boolean {
    const navError = event as { error?: Error };
    if (!navError.error) return false;

    const message = navError.error.message?.toLowerCase() || '';
    const name = navError.error.name?.toLowerCase() || '';

    return (
      name === 'typeerror' &&
      (message.includes('failed to fetch') ||
        message.includes('dynamically imported module') ||
        message.includes('importing a module script failed') ||
        message.includes('chunk'))
    );
  }

  #isChunkLoadRejection(event: PromiseRejectionEvent): boolean {
    const reason = event.reason;
    if (!reason) return false;

    if (reason instanceof TypeError) {
      const message = reason.message?.toLowerCase() || '';
      return (
        message.includes('failed to fetch') ||
        message.includes('importing a module script failed') ||
        message.includes('dynamically imported module')
      );
    }

    if (reason instanceof Error) {
      const message = reason.message?.toLowerCase() || '';
      return message.includes('chunk') || message.includes('failed to fetch');
    }

    if (typeof reason === 'string') {
      const message = reason.toLowerCase();
      return (
        message.includes('chunk') ||
        message.includes('failed to fetch') ||
        message.includes('module')
      );
    }

    return false;
  }

  #clearRetryIfVersionChanged(): void {
    const sessionStorage = this.#window?.sessionStorage;
    if (!sessionStorage) return;

    const storedVersion = sessionStorage.getItem(this.#VERSION_KEY);

    if (storedVersion !== this.#appVersion) {
      sessionStorage.removeItem(this.#RETRY_KEY);
      sessionStorage.removeItem(this.#RETRY_TIMESTAMP_KEY);
      sessionStorage.setItem(this.#VERSION_KEY, this.#appVersion);
    }
  }

  #handleChunkError(): void {
    const retryCount = this.#getRetryCount();
    const lastRetryTime = this.#getLastRetryTime();

    if (lastRetryTime && Date.now() - lastRetryTime < this.#RETRY_DELAY_MS) {
      this.#showNetworkError();
      return;
    }

    if (retryCount >= this.#MAX_RETRIES) {
      this.#showMaxRetriesError();
      return;
    }

    this.#incrementRetryCount(retryCount);
    this.#showReloadNotification();
  }

  #getRetryCount(): number {
    const sessionStorage = this.#window?.sessionStorage;
    if (!sessionStorage) return 0;

    const value = sessionStorage.getItem(this.#RETRY_KEY);
    return value ? parseInt(value, 10) : 0;
  }

  #getLastRetryTime(): number | null {
    const sessionStorage = this.#window?.sessionStorage;
    if (!sessionStorage) return null;

    const value = sessionStorage.getItem(this.#RETRY_TIMESTAMP_KEY);
    return value ? parseInt(value, 10) : null;
  }

  #incrementRetryCount(current: number): void {
    const sessionStorage = this.#window?.sessionStorage;
    if (!sessionStorage) return;

    sessionStorage.setItem(this.#RETRY_KEY, String(current + 1));
    sessionStorage.setItem(this.#RETRY_TIMESTAMP_KEY, String(Date.now()));
  }

  #showReloadNotification(): void {
    this.#dialog
      .open(DeploymentRefreshDialogComponent)
      .afterClosed()
      .subscribe(() => {
        this.#window?.location.reload();
      });
  }

  #showMaxRetriesError(): void {
    this.#alert.showError(
      'No se pudo cargar la aplicación. Por favor, recarga la página manualmente.',
      { duration: 0 },
    );
  }

  #showNetworkError(): void {
    this.#alert.showError('Problema de conexión. Intenta recargar la página.', {
      duration: 0,
    });
  }
}
